export type CallsRuntimeOutcome =
  | 'success'
  | 'progress'
  | 'timeout'
  | 'rejected'
  | 'failure'
  | 'recovery'
  | 'unknown';

export type CallsRuntimeSeverity =
  | 'healthy'
  | 'informational'
  | 'warning'
  | 'critical'
  | 'unknown';

export type CallsExecutiveDecision =
  | 'accept-runtime-evidence'
  | 'continue-observation'
  | 'record-timeout'
  | 'record-rejection'
  | 'open-runtime-incident'
  | 'record-recovery'
  | 'withhold-decision';

export type CallsRuntimeDecisionProjection = Readonly<{
  title: string;
  owner: string;
  operation: string;
  stage: string;
  status: string;
  source: string;
  mode: string;
  certificationDecision: string;
  certificationAllowed: boolean;
  runtimeAllowed: boolean;
  reasonCode: string;
  sharedEvidencePublished: boolean;
  executiveRuntimeRecorded: boolean;
  callSessionId: string | null;
  callId: string | null;
  observedAt: string;
  outcome: CallsRuntimeOutcome;
  severity: CallsRuntimeSeverity;
  executiveDecision: CallsExecutiveDecision;
  healthImpact: number;
}>;

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord =>
  value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' && value.trim().length > 0
    ? value
    : fallback;

const asNullableString = (
  value: unknown,
): string | null =>
  typeof value === 'string' && value.trim().length > 0
    ? value
    : null;

const asExplicitBoolean = (
  value: unknown,
): boolean => value === true;

const normalizeSignal = (...values: unknown[]): string =>
  values
    .filter(
      (value): value is string =>
        typeof value === 'string',
    )
    .join(' ')
    .toLowerCase();

const classifyOutcome = (
  stage: string,
  status: string,
  reasonCode: string,
): CallsRuntimeOutcome => {
  const signal = normalizeSignal(
    stage,
    status,
    reasonCode,
  );

  if (
    signal.includes('recover') ||
    signal.includes('restored') ||
    signal.includes('reconnected')
  ) {
    return 'recovery';
  }

  if (
    signal.includes('timeout') ||
    signal.includes('timed out') ||
    signal.includes('unanswered') ||
    signal.includes('no answer')
  ) {
    return 'timeout';
  }

  if (
    signal.includes('reject') ||
    signal.includes('declin') ||
    signal.includes('denied') ||
    signal.includes('blocked')
  ) {
    return 'rejected';
  }

  if (
    signal.includes('fail') ||
    signal.includes('error') ||
    signal.includes('disconnect') ||
    signal.includes('terminated unexpectedly')
  ) {
    return 'failure';
  }

  if (
    signal.includes('success') ||
    signal.includes('connected') ||
    signal.includes('answered') ||
    signal.includes('completed')
  ) {
    return 'success';
  }

  if (
    signal.includes('start') ||
    signal.includes('ring') ||
    signal.includes('incoming') ||
    signal.includes('outgoing') ||
    signal.includes('progress') ||
    signal.includes('observed') ||
    signal.includes('authorized')
  ) {
    return 'progress';
  }

  return 'unknown';
};

const decisionForOutcome = (
  outcome: CallsRuntimeOutcome,
): {
  severity: CallsRuntimeSeverity;
  executiveDecision: CallsExecutiveDecision;
  healthImpact: number;
} => {
  switch (outcome) {
    case 'success':
      return {
        severity: 'healthy',
        executiveDecision:
          'accept-runtime-evidence',
        healthImpact: 1,
      };

    case 'progress':
      return {
        severity: 'informational',
        executiveDecision:
          'continue-observation',
        healthImpact: 0,
      };

    case 'timeout':
      return {
        severity: 'warning',
        executiveDecision: 'record-timeout',
        healthImpact: -1,
      };

    case 'rejected':
      return {
        severity: 'informational',
        executiveDecision:
          'record-rejection',
        healthImpact: 0,
      };

    case 'failure':
      return {
        severity: 'critical',
        executiveDecision:
          'open-runtime-incident',
        healthImpact: -2,
      };

    case 'recovery':
      return {
        severity: 'healthy',
        executiveDecision:
          'record-recovery',
        healthImpact: 1,
      };

    default:
      return {
        severity: 'unknown',
        executiveDecision:
          'withhold-decision',
        healthImpact: 0,
      };
  }
};

export function projectCallsRuntimeDecision(
  actionValue: unknown,
): CallsRuntimeDecisionProjection {
  const action = asRecord(actionValue);
  const detail = asRecord(action.detail);

  const stage = asString(
    detail.stage,
    'CALLS_RUNTIME_EVENT',
  );

  const status = asString(
    detail.status,
    'unknown',
  );

  const source = asString(
    detail.source,
    asString(detail.owner, 'Calls Governor'),
  );

  const reasonCode = asString(
    detail.reasonCode,
    asString(
      detail.reason,
      asString(
        detail.error,
        'No failure reason recorded',
      ),
    ),
  );

  const outcome = classifyOutcome(
    stage,
    status,
    reasonCode,
  );

  const decision = decisionForOutcome(outcome);

  return Object.freeze({
    title: stage,
    owner: source,
    operation: asString(
      detail.operation,
      stage,
    ),
    stage,
    status,
    source,
    mode: asString(
      detail.observationMode,
      asString(detail.mode, 'observe'),
    ),
    certificationDecision: asString(
      detail.certificationDecision,
      'unverified',
    ),

    // Authority is never inferred from status or outcome.
    certificationAllowed:
      asExplicitBoolean(
        detail.certificationAllowed,
      ),
    runtimeAllowed:
      asExplicitBoolean(detail.runtimeAllowed),

    reasonCode,
    sharedEvidencePublished:
      asNullableString(
        detail.sharedEvidenceId,
      ) !== null,
    executiveRuntimeRecorded:
      asExplicitBoolean(
        detail.runtimeEvidenceRecorded,
      ),
    callSessionId:
      asNullableString(detail.callSessionId),
    callId:
      asNullableString(detail.callId),
    observedAt: asString(
      detail.timestamp,
      asString(action.createdAt, 'Unknown'),
    ),
    outcome,
    severity: decision.severity,
    executiveDecision:
      decision.executiveDecision,
    healthImpact: decision.healthImpact,
  });
}
